/**
 * PRODUCTION-GRADE CENTRALIZED API CLIENT
 * 
 * This is the ONLY place where Authorization headers should be attached.
 * All API calls across the app MUST use this client.
 * 
 * Features:
 * - Automatic Authorization header attachment via interceptor
 * - Token validation (filters out "undefined", "null", empty strings)
 * - 401 response handling with single redirect (no loops)
 * - Request cancellation support via AbortController
 * - Circuit breaker for degraded backend
 * - Exponential backoff retry (GET only, max 2 retries)
 * - Request ID tracking for debugging
 * 
 * Usage:
 *   import api from '../utils/axiosClient';
 *   const response = await api.get('/api/leads');
 *   const data = await api.post('/api/leads', { name: 'John' });
 */

import axios from 'axios';
import { BUILD_VERSION } from '../buildVersion';

// Get dynamic backend URL - uses window.location.origin in production
const getBackendUrl = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    }
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

// Track if we've already redirected to prevent loops
let isRedirectingToLogin = false;
let lastTokenClearTime = 0;

// Circuit breaker state
const circuitBreaker = {
  failures: 0,
  lastFailure: 0,
  isOpen: false,
  threshold: 5,
  resetTimeout: 60000, // 60 seconds
};

/**
 * Validate token - returns true only for valid tokens
 */
const isValidToken = (token) => {
  if (!token) return false;
  if (token === 'undefined' || token === 'null') return false;
  if (token.trim() === '') return false;
  // Basic JWT structure check (3 parts separated by dots)
  const parts = token.split('.');
  if (parts.length !== 3) return false;
  return true;
};

/**
 * Get current auth token from localStorage
 */
const getAuthToken = () => {
  try {
    const token = localStorage.getItem('token');
    return isValidToken(token) ? token : null;
  } catch (e) {
    return null;
  }
};

/**
 * Clear auth state (called on 401)
 */
const clearAuthState = () => {
  // Prevent multiple clears within 5 seconds
  const now = Date.now();
  if (now - lastTokenClearTime < 5000) {
    console.debug('[API] Skipping duplicate token clear');
    return;
  }
  lastTokenClearTime = now;
  
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    console.info('[API] Auth state cleared due to 401');
  } catch (e) {
    console.error('[API] Failed to clear auth state:', e);
  }
};

/**
 * Redirect to login (called once on 401)
 */
const redirectToLogin = () => {
  if (isRedirectingToLogin) {
    console.debug('[API] Already redirecting to login, skipping');
    return;
  }
  
  // Don't redirect if already on login page
  if (typeof window !== 'undefined' && window.location.pathname === '/login') {
    return;
  }
  
  isRedirectingToLogin = true;
  console.info('[API] Redirecting to login');
  
  // Use setTimeout to allow current request to complete
  setTimeout(() => {
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }, 100);
};

/**
 * Check circuit breaker state
 */
const checkCircuitBreaker = () => {
  if (!circuitBreaker.isOpen) return true;
  
  const timeSinceLastFailure = Date.now() - circuitBreaker.lastFailure;
  if (timeSinceLastFailure > circuitBreaker.resetTimeout) {
    console.info('[API] Circuit breaker half-open, allowing request');
    return true;
  }
  
  return false;
};

/**
 * Record failure for circuit breaker
 */
const recordFailure = () => {
  circuitBreaker.failures++;
  circuitBreaker.lastFailure = Date.now();
  
  if (circuitBreaker.failures >= circuitBreaker.threshold) {
    circuitBreaker.isOpen = true;
    console.warn('[API] Circuit breaker OPEN after', circuitBreaker.failures, 'failures');
  }
};

/**
 * Record success for circuit breaker
 */
const recordSuccess = () => {
  if (circuitBreaker.isOpen) {
    console.info('[API] Circuit breaker CLOSED after successful request');
  }
  circuitBreaker.failures = 0;
  circuitBreaker.isOpen = false;
};

/**
 * Generate request ID for tracking
 */
const generateRequestId = () => {
  return Math.random().toString(36).substring(2, 10);
};

// Create axios instance
const api = axios.create({
  baseURL: getBackendUrl(),
  timeout: 30000, // 30 second default timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Update baseURL dynamically (for production where it needs to be window.location.origin)
api.interceptors.request.use((config) => {
  // Always use dynamic baseURL
  config.baseURL = getBackendUrl();
  return config;
});

/**
 * REQUEST INTERCEPTOR - Attaches Authorization header automatically
 * This is the ONLY place where Authorization headers should be set.
 */
api.interceptors.request.use(
  (config) => {
    // Add request ID for tracking
    const requestId = generateRequestId();
    config.headers['X-Request-ID'] = requestId;
    config.metadata = { requestId, startTime: Date.now() };
    
    // P0 HOTFIX: Add X-App-Version header for write requests
    const method = (config.method || 'GET').toUpperCase();
    const isWriteRequest = ['PUT', 'POST', 'DELETE', 'PATCH'].includes(method);
    if (isWriteRequest && BUILD_VERSION) {
      config.headers['X-App-Version'] = BUILD_VERSION;
    }
    
    // Check circuit breaker
    if (!checkCircuitBreaker()) {
      return Promise.reject(new Error('Service temporarily unavailable (circuit breaker open)'));
    }
    
    // Get token and attach if valid
    const token = getAuthToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      // Remove Authorization header if no valid token
      delete config.headers['Authorization'];
    }
    
    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.debug(`[API] ${config.method?.toUpperCase()} ${config.url}`, {
        requestId,
        hasAuth: !!token,
        appVersion: BUILD_VERSION,
      });
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * RESPONSE INTERCEPTOR - Handles 401 and other errors
 */
api.interceptors.response.use(
  (response) => {
    recordSuccess();
    
    // Log response time in development
    if (process.env.NODE_ENV === 'development' && response.config.metadata) {
      const duration = Date.now() - response.config.metadata.startTime;
      console.debug(`[API] ✓ ${response.config.url} (${duration}ms)`);
    }
    
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const requestId = error.config?.metadata?.requestId;
    
    // Handle 401 - Unauthorized
    if (status === 401) {
      console.warn(`[API] 401 Unauthorized (${requestId}):`, error.config?.url);
      
      // Clear auth state once
      clearAuthState();
      
      // Redirect to login (once)
      redirectToLogin();
      
      // Return a controlled error
      return Promise.reject({
        ...error,
        isAuthError: true,
        message: 'Session expired. Please log in again.',
      });
    }
    
    // Handle 403 - Forbidden (valid token, but no permission)
    if (status === 403) {
      console.debug(`[API] 403 Forbidden (${requestId}):`, error.config?.url);
      // Don't clear auth, don't redirect - just return the error
      return Promise.reject({
        ...error,
        isPermissionError: true,
        message: 'You do not have permission to access this resource.',
      });
    }
    
    // Handle 502/520/503 - Backend errors
    if (status === 502 || status === 520 || status === 503) {
      recordFailure();
      console.error(`[API] Backend error ${status} (${requestId}):`, error.config?.url);
      return Promise.reject({
        ...error,
        isBackendError: true,
        message: 'Service temporarily unavailable. Please try again.',
      });
    }
    
    // Handle network errors
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
      recordFailure();
      console.error(`[API] Network error (${requestId}):`, error.config?.url);
      return Promise.reject({
        ...error,
        isNetworkError: true,
        message: 'Network error. Please check your connection.',
      });
    }
    
    return Promise.reject(error);
  }
);

/**
 * GET with retry (max 3 retries with exponential backoff)
 * Handles network errors, HTTP2 protocol errors, and timeouts
 */
api.getWithRetry = async (url, config = {}, maxRetries = 3) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await api.get(url, config);
    } catch (error) {
      lastError = error;
      
      // Don't retry auth errors or permission errors
      if (error.isAuthError || error.isPermissionError) {
        throw error;
      }
      
      // Don't retry if we've exhausted retries
      if (attempt >= maxRetries) {
        console.warn(`[API] Max retries reached for ${url}`);
        throw error;
      }
      
      // Check if this is a retryable error
      const isRetryable = 
        error.isNetworkError || 
        error.code === 'ERR_NETWORK' || 
        error.code === 'ECONNABORTED' ||
        error.code === 'ERR_HTTP2_PROTOCOL_ERROR' ||
        error.message?.includes('HTTP2') ||
        error.message?.includes('network') ||
        (error.response?.status >= 500 && error.response?.status < 600);
      
      if (!isRetryable) {
        throw error;
      }
      
      // Exponential backoff: 1s, 2s, 4s, 8s
      const delay = Math.pow(2, attempt) * 1000;
      console.info(`[API] Retry ${attempt + 1}/${maxRetries} for ${url} in ${delay}ms (${error.code || error.message})`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
};

/**
 * POST with idempotency key
 */
api.postIdempotent = async (url, data = {}, config = {}) => {
  const idempotencyKey = config.idempotencyKey || `${Date.now()}-${generateRequestId()}`;
  
  return api.post(url, data, {
    ...config,
    headers: {
      ...config.headers,
      'X-Idempotency-Key': idempotencyKey,
    },
  });
};

/**
 * Create AbortController for request cancellation
 */
api.createCancelToken = () => {
  const controller = new AbortController();
  return {
    signal: controller.signal,
    cancel: () => controller.abort(),
  };
};

/**
 * Reset redirect flag (call this on successful login)
 */
export const resetAuthRedirectFlag = () => {
  isRedirectingToLogin = false;
  lastTokenClearTime = 0;
};

/**
 * Get circuit breaker status
 */
export const getCircuitBreakerStatus = () => ({
  isOpen: circuitBreaker.isOpen,
  failures: circuitBreaker.failures,
  lastFailure: circuitBreaker.lastFailure,
});

export default api;
